import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { CreateFastMenuActionItemRequest, UpdateFastMenuActionItemRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useCreateButtonOption = () => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: CreateFastMenuActionItemRequest) => api.postFastActionItemsV1(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PLUS_BUTTON_OPTIONS_LIST(projectId) });
      showFlashToast({ variant: "success", message: t("page.plus-button-links.create.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.plus-button-links.create.error") });
    },
  });
};

const useEditButtonOption = () => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateFastMenuActionItemRequest }) =>
      api.putFastActionItemsV1(id, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PLUS_BUTTON_OPTIONS_LIST(projectId) });
      showFlashToast({ variant: "success", message: t("page.plus-button-links.edit.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.plus-button-links.edit.error") });
    },
  });
};

const useDeleteButtonOption = () => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: string) => api.deleteFastActionItemsByIdV1(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PLUS_BUTTON_OPTIONS_LIST(projectId) });
      showFlashToast({ variant: "success", message: t("page.plus-button-links.delete.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.plus-button-links.delete.error") });
    },
  });
};

export const plusButtonMutations = {
  useCreateButtonOption,
  useEditButtonOption,
  useDeleteButtonOption,
};
